const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ApiError {
  error: string;
  message?: string;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('arena_token');
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      error: 'Unknown error',
    }));
    throw new Error(error.error || error.message || 'Request failed');
  }

  return response.json();
}

// Types
export interface Model {
  id: string;
  display_name: string;
  description?: string;
  source_url?: string;
  elo_rating: number;
  wins: number;
  losses: number;
  ties: number;
  total_battles: number;
  win_rate: number;
  rank?: number;
}

export interface Clip {
  id: string;
  title: string;
  thumbnail_url?: string;
  video_url: string;
  duration_seconds: number;
  source: string;
  source_url?: string;
}

export interface ModelDetection {
  id: string;
  position: 'left' | 'right';
  shots: Shot[];
  total_shots: number;
  processing_time_ms: number | null;
}

export interface Battle {
  battle_id: string;
  clip_id: string;
  video_url: string;
  thumbnail_url?: string;
  duration_seconds: number;
  model_a: ModelDetection;
  model_b: ModelDetection;
}

export interface Shot {
  start_time: number;
  end_time: number;
  confidence?: number;
}

export interface VoteResult {
  battle_id: string;
  your_vote: 'model_a' | 'model_b' | 'tie' | 'skip';
  reveal: {
    model_a: {
      model_id: string;
      display_name: string;
      elo_rating: number;
      source_url?: string;
    };
    model_b: {
      model_id: string;
      display_name: string;
      elo_rating: number;
      source_url?: string;
    };
    winner: string;
  };
  stats: {
    total_votes: number;
    model_a_wins_percent: number;
    model_b_wins_percent: number;
    ties_percent: number;
  };
}

export interface Leaderboard {
  leaderboard: Model[];
  total_votes: number;
  updated_at: string;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  picture: string | null;
  role: string;
  vote_count: number;
  vote_streak: number;
  created_at: string;
}

export interface UserProfile {
  user: AuthUser;
  stats: {
    total_votes: number;
    accuracy_vs_consensus: number;
    favorite_model: string | null;
    member_since: string;
  };
}

export interface VoteHistory {
  votes: Array<{
    battle_id: string;
    clip_title: string;
    your_vote: string;
    model_a_name: string;
    model_b_name: string;
    winner_model_id: string | null;
    created_at: string;
  }>;
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface Upload {
  id: string;
  title: string;
  status: 'pending_review' | 'approved' | 'rejected';
  file_size_bytes: number;
  duration_seconds: number | null;
  created_at: string;
  reviewed_at: string | null;
}

// API Functions
export const api = {
  // Battles
  createBattle: (clipId?: string) =>
    fetchApi<Battle>('/battles', {
      method: 'POST',
      body: JSON.stringify(clipId ? { clip_id: clipId } : {}),
    }),

  getBattle: (battleId: string) =>
    fetchApi<Battle>(`/battles/${battleId}`),

  // Votes
  submitVote: (battleId: string, winner: 'model_a' | 'model_b' | 'tie' | 'skip') =>
    fetchApi<VoteResult>('/votes', {
      method: 'POST',
      body: JSON.stringify({ battle_id: battleId, winner }),
    }),

  // Leaderboard
  getLeaderboard: () =>
    fetchApi<Leaderboard>('/leaderboard'),

  getModelStats: (modelId: string) =>
    fetchApi<{ model: Model; head_to_head: any[]; recent_battles: any[] }>(`/leaderboard/${modelId}`),

  // Clips/Samples
  getSamples: (limit = 10, offset = 0) =>
    fetchApi<{ samples: Clip[]; total: number }>(`/samples?limit=${limit}&offset=${offset}`),

  // Auth
  getMe: () =>
    fetchApi<{ authenticated: boolean; user?: AuthUser }>('/auth/me'),

  logout: () =>
    fetchApi<{ success: boolean }>('/auth/logout', { method: 'POST' }),

  // Profile
  getProfile: () =>
    fetchApi<UserProfile>('/profile'),

  getVoteHistory: (limit = 20, offset = 0) =>
    fetchApi<VoteHistory>(`/profile/votes?limit=${limit}&offset=${offset}`),

  getProfileStats: () =>
    fetchApi<any>('/profile/stats'),

  getPublicProfile: (userId: string) =>
    fetchApi<any>(`/profile/${userId}/public`),

  // Uploads
  getUploads: () =>
    fetchApi<{ uploads: Upload[] }>('/uploads'),

  // Admin
  getAdminClips: () =>
    fetchApi<{ clips: any[] }>('/admin/clips'),

  updateAdminClip: (clipId: string, data: Record<string, any>) =>
    fetchApi<any>(`/admin/clips/${clipId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getAdminModels: () =>
    fetchApi<{ models: any[] }>('/admin/models'),

  updateAdminModel: (modelId: string, data: Record<string, any>) =>
    fetchApi<any>(`/admin/models/${modelId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getAdminUsers: (limit = 50, offset = 0) =>
    fetchApi<{ users: any[]; total: number }>(`/admin/users?limit=${limit}&offset=${offset}`),

  updateUserRole: (userId: string, role: 'admin' | 'user') =>
    fetchApi<any>(`/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),

  getAdminAnalytics: (days = 7) =>
    fetchApi<any>(`/admin/analytics?days=${days}`),

  getAdminUploads: (status = 'pending_review') =>
    fetchApi<{ uploads: any[] }>(`/admin/uploads?status=${status}`),

  reviewUpload: (uploadId: string, status: 'approved' | 'rejected') =>
    fetchApi<any>(`/admin/uploads/${uploadId}/review`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),
};
